import { useState } from "react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Sparkle, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AlumniResultCard from "@/components/AlumniResultCard";

const EXAMPLES = [
  "Product managers from IIT Bombay in Bangalore, batch of 2015",
  "Doctors who studied at AIIMS",
  "Software engineers skilled in React and Python",
  "Alumni of Government Model Engineering College",
  "Data scientists in Mumbai",
];

const FILTER_LABELS = {
  name: "Name", institution: "Institution", location: "Location", course: "Course",
  grad_year: "Year", batch: "Batch", profession: "Role", skills: "Skills",
};

export default function AISearch() {
  useSeo("Search", null, null, { noindex: true });
  const [q, setQ] = useState("");
  const [state, setState] = useState({ status: "idle" });

  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setQ(query);
    setState({ status: "loading" });
    try {
      const { data } = await api.post("/ai-search", { query });
      setState({ status: "done", ...data });
    } catch (e) {
      setState({ status: "error", message: e?.response?.data?.detail || "Search failed. Please try again." });
    }
  };

  return (
    <main className="mx-auto max-w-5xl px-5 py-10" data-testid="ai-search-page">
      <div className="flex items-center gap-2">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary text-primary-foreground"><Sparkle size={22} weight="fill" /></span>
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">AI Alumni Search</h1>
          <p className="text-sm text-muted-foreground">Describe who you're looking for in plain English.</p>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-6">
        <div className="relative">
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Marketing leads from Delhi University, class of 2010"
            className="w-full rounded-full border border-input bg-card py-4 pl-12 pr-32 text-base shadow-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-ring/40"
            data-testid="ai-search-input"
          />
          <Button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 gap-1.5 rounded-full" disabled={state.status === "loading"} data-testid="ai-search-submit">
            {state.status === "loading" ? "Searching…" : <>Search <ArrowRight size={16} /></>}
          </Button>
        </div>
      </form>

      {state.status === "idle" && (
        <div className="mt-6" data-testid="ai-search-examples">
          <p className="mb-2 text-sm text-muted-foreground">Try one of these:</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => run(ex)}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground transition-colors hover:border-primary hover:bg-accent">
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {state.status === "error" && <p className="mt-8 rounded-xl bg-destructive/10 p-4 text-destructive" data-testid="ai-search-error">{state.message}</p>}

      {state.status === "loading" && (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-muted" />)}
        </div>
      )}

      {state.status === "done" && (
        <div className="mt-8" data-testid="ai-search-results">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-heading text-lg font-medium" data-testid="ai-search-answer">{state.answer}</p>
          </div>
          {state.interpreted && Object.keys(state.interpreted).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ai-search-interpreted">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Understood as</span>
              {Object.entries(state.interpreted).map(([k, v]) => (
                <Badge key={k} variant="outline" className="rounded-full font-normal">
                  {FILTER_LABELS[k] || k}: {Array.isArray(v) ? v.join(", ") : v}
                </Badge>
              ))}
            </div>
          )}
          {state.results.length === 0 ? (
            <p className="mt-8 rounded-2xl border border-dashed border-border py-14 text-center text-muted-foreground">No matches yet. Try fewer details or different wording.</p>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {state.results.map((p, i) => <AlumniResultCard key={p.user_id} p={p} index={i} />)}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
